import {
  Calendar,
  EventPropGetter,
  Views,
  momentLocalizer,
  ResourceHeaderProps,
  CalendarProps,
} from "react-big-calendar";
import moment from "moment";
import { CalendarToolbar } from "./CalendarToolbar";
import "moment/locale/ja";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "@/common/calendar/index.css";
import { useCallback } from "react";

// 背景色をゼブラ表示する設定
const slotPropGetter = (date: Date) => ({
  className: "slotDefault",
  style: {
    backgroundColor: moment(date).hour() % 2 ? "#eaf0ff" : "#ffffff",
    color: "black",
  },
});

// defaultDateは初期表示の日付、formatsは日本語化
const localizer = momentLocalizer(moment);
const formats = {
  dayHeaderFormat: "M月D日(ddd)",
} as const;
const views = [Views.DAY];
const scrollTo = {
  get now() {
    return new Date(1970, 1, 1, new Date().getHours() - 3);
  },
} as const;

export type GxBigCalendarHeader = {
  toolbar: () => null;
  resourceHeader?: (props: ResourceHeaderProps) => JSX.Element;
};

const onNavigate = () => { };

export type GxBigCalendarEvent<TColor extends string> = {
  id: number;
  title: string;
  titleName?: string;
  color: TColor;
  start: string | Date;
  end: string | Date;
  resourceId: number;
};

export type GxBigCalendarEventProp = {
  backgroundColor: string;
  fontColor: string;
  customEventTimeRange?: string;
};

export type GxBigCalendarEventPropGetter<T extends string> = (
  event: GxBigCalendarEvent<T>
) => GxBigCalendarEventProp;

export type GxBigCalendarResource = {
  headerCode: string;
  resourceId: number;
};

export type GXResourceHeaderProps<GxBigCalendarResource> = {
  label: React.ReactNode;
  index: number;
  resource: GxBigCalendarResource;
};

export type GxBigCalendarProps<
  TColor extends string,
  TEvent extends GxBigCalendarEvent<TColor>,
  TResource extends GxBigCalendarResource
> = {
  targetDateSelected?: string;
  events?: TEvent[];
  resources?: TResource[];
  onChangeDate: (date: string) => void;
  onEventSelected?: (event: TEvent) => void;
  eventPropGetter?: GxBigCalendarEventPropGetter<TColor>;
  components: GxBigCalendarHeader;
  tooltipAccessor?: CalendarProps<TEvent, TResource>["tooltipAccessor"];
};
export const GxBigCalendar = <
  TColor extends string,
  TEvent extends GxBigCalendarEvent<TColor>,
  TResource extends GxBigCalendarResource
>({
  targetDateSelected,
  events,
  resources,
  onChangeDate,
  onEventSelected,
  eventPropGetter,
  components,
  tooltipAccessor,
}: GxBigCalendarProps<TColor, TEvent, TResource>) => {
  const eventPropGetterWrapper = useCallback<
    EventPropGetter<GxBigCalendarEvent<TColor>>
  >(
    (event) => {
      const props = eventPropGetter && eventPropGetter(event);
      return {
        style: {
          backgroundColor: props?.backgroundColor,
          color: props?.fontColor,
          "--custom-event-time-range": props?.customEventTimeRange?.length
            ? `"${props?.customEventTimeRange}"`
            : undefined,
        },
        className: props?.customEventTimeRange?.length
          ? "custom-event-time-range"
          : undefined,
      };
    },
    [eventPropGetter]
  );

  return (
    <>
      <CalendarToolbar
        onChangeDate={onChangeDate}
        targetDateSelected={targetDateSelected}
      />
      <Calendar
        localizer={localizer}
        date={targetDateSelected}
        events={events}
        onNavigate={onNavigate}
        onSelectEvent={onEventSelected}
        views={views}
        step={30}
        timeslots={2}
        eventPropGetter={eventPropGetterWrapper}
        slotPropGetter={slotPropGetter}
        showMultiDayTimes
        formats={formats}
        scrollToTime={scrollTo.now}
        components={components}
        startAccessor="start"
        endAccessor="end"
        resources={resources}
        resourceIdAccessor="resourceId"
        resourceTitleAccessor="headerCode"
        defaultView={Views.DAY}
        tooltipAccessor={tooltipAccessor}
      />
    </>
  );
};
